import React, { useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  SxProps,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAlert } from "../context/AlertContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updatePasswordApi } from "../api/requests/resetPassword";
import { BLUE, GRAY, WHITE, PasswordErrMsg } from "../utils/constants";
import { LOGINPATH } from "../routes/routePaths";
import { PasswordCriteriaMarking, passwordObj } from "./CheckPasswordCriteria";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
    strongPassword: "",
  });
  const [passwordStatus, setPasswordStatus] = useState("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const getErrormessage = async (passwordCheckObj: passwordObj) => {
    const { upper, lower, num, min, special } = passwordCheckObj;
    let errMsg = "";
    let isStrongPassword = false;
    if (!min) {
      errMsg = PasswordErrMsg.minCharacter;
      isStrongPassword = false;
    } else if (!upper) {
      errMsg = PasswordErrMsg.upperCase;
      isStrongPassword = false;
    } else if (!lower) {
      errMsg = PasswordErrMsg.lowerCase;
      isStrongPassword = false;
    } else if (!num) {
      errMsg = PasswordErrMsg.digits;
      isStrongPassword = false;
    } else if (!special) {
      errMsg = PasswordErrMsg.specialChar;
      isStrongPassword = false;
    } else {
      errMsg = "";
      isStrongPassword = true;
    }
    return { isStrongPassword, errMsg };
  };

  const validatePasswords = async (): Promise<boolean> => {
    const newPassword = newPasswordRef.current?.value || "";
    const confirmPassword = confirmPasswordRef.current?.value || "";
    let valid = true;
    const newErrors = {
      newPassword: "",
      confirmPassword: "",
      strongPassword: "",
    };
    setPasswordStatus("");

    if (!newPassword) {
      newErrors.newPassword = "Enter new password";
      valid = false;
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    } else {
      const passwordCheckResult = await PasswordCriteriaMarking(newPassword);
      const result = await getErrormessage(passwordCheckResult);
      if (result.isStrongPassword) {
        setPasswordStatus(PasswordErrMsg.strongPassword);
      } else {
        newErrors.strongPassword = result.errMsg;
        setPasswordStatus("");
        valid = false;
      }
    }
    setErrors(newErrors);
    console.log("Validation result:", {
      errors: newErrors,
      status: passwordStatus,
      valid,
    }); // For testing
    return valid;
  };

  const handleUpdatePassword = async () => {
    if (!token) {
      showAlert("Invalid or missing token.", "error");
      return;
    }

    if (!(await validatePasswords())) {
      return; // Stop if validation fails
    }

    const newPassword = newPasswordRef.current?.value || "";
    setLoader(true);
    try {
      const response = await updatePasswordApi({ token, newPassword });
      if (response?.status === 200) {
        showAlert("Password updated successfully!", "success");
        navigate(LOGINPATH);
      } else {
        showAlert(
          response?.data?.message || "Failed to update password.",
          "error"
        );
      }
    } catch {
      showAlert("An error occurred. Please try again.", "error");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box sx={outerBoxStyles}>
      <Box sx={innerBoxStyles}>
        <Typography variant="h5" sx={titleStyles}>
          Update Password
        </Typography>
        <Typography variant="body1" sx={subtitleStyles}>
          Enter your new password below.
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter new password"
          inputRef={newPasswordRef}
          type={showNewPassword ? "text" : "password"}
          sx={textFieldStyles}
          error={!!errors.newPassword}
          helperText={errors.newPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          placeholder="Confirm new password"
          inputRef={confirmPasswordRef}
          type={showConfirmPassword ? "text" : "password"}
          sx={textFieldStyles}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {errors.strongPassword && (
          <Typography variant="body2" sx={{ color: "red", mb: 2 }}>
            {errors.strongPassword}
          </Typography>
        )}
        {passwordStatus && (
          <Typography variant="body2" sx={{ color: "green", mb: 2 }}>
            {passwordStatus}
          </Typography>
        )}
        <Button
          onClick={handleUpdatePassword}
          fullWidth
          variant="contained"
          sx={buttonStyles}
          disabled={loader}
        >
          {loader ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Update Password"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ResetPassword;

const outerBoxStyles: SxProps = {
  flexGrow: 1,
  height: "100%",
  bgcolor: WHITE.moderate,
  borderRadius: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const innerBoxStyles: SxProps = {
  bgcolor: WHITE.main,
  width: "510px",
  minHeight: "300px",
  p: 4,
  m: 8,
  borderRadius: "20px",
  justifyContent: "space-evenly",
};

const titleStyles: SxProps = {
  fontSize: "23.8px",
  fontFamily: "Poppins, sans-serif",
  mb: 2,
};

const subtitleStyles: SxProps = {
  color: GRAY.dark,
  fontSize: "16px",
  fontFamily: "Poppins, sans-serif",
  mb: 4,
};

const textFieldStyles: SxProps = {
  height: "69.69999694824219px",
  bgcolor: GRAY.light,
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px",
    fontFamily: "Poppins, sans-serif",
    height: "69.69999694824219px",
    "& fieldset": {
      borderColor: "transparent",
    },
  },
  borderRadius: "11px",
  mb: 2,
};

const buttonStyles: SxProps = {
  bgcolor: BLUE.dark,
  mt: 2,
  height: "55px",
  borderRadius: "13.33px",
  fontSize: "22px",
  textTransform: "none",
};
